import { forwardRef, useLayoutEffect, useRef, useState } from "react";

import { AnimatePresence, motion } from "framer-motion";

import "./FnrHoverButton.scss";

const FnrHoverButton = forwardRef(
  (
    {
      children,
      selected,
      onClick,
      forceHover = false,
      disabled,
      borderRadius = 9,
      bgColor,
      hoverOpacity = 1,
      style,
      ...rest
    },
    forwardedRef
  ) => {
    const [shouldHover, setShouldHover] = useState(false);
    const [hovering, setHovering] = useState(false);
    const [hoverAnimations, setHoverAnimation] = useState(false);
    const [selecting, setSelecting] = useState(false);
    const [selectedAnimation, setSelectedAnimation] = useState(false);

    const localRef = useRef();
    const buttonRef = forwardedRef || localRef;
    const pointerPosition = useRef({});
    const setPointerPosition = (newPosition) => {
      pointerPosition.current = newPosition;
    };
    const updatePointerPositionFromMouseEvent = (evt) =>
      setPointerPosition({ x: evt.clientX, y: evt.clientY });

    const onHoverStart = (event, info) => {
      setPointerPosition(info.point);
      setShouldHover(true);
    };

    const onHoverEnd = (event, info) => {
      setPointerPosition(info.point);
      setShouldHover(false);
    };

    const getPointerOffset = (keepInBounds = false) => {
      const buttonDimensions = buttonRef.current.getBoundingClientRect();
      const offset = {
        x: pointerPosition.current.x - buttonDimensions.left,
        y: pointerPosition.current.y - buttonDimensions.top,
      };

      if (keepInBounds) {
        if (offset.x < 0) offset.x = 0;
        else if (offset.x > buttonDimensions.width)
          offset.x = buttonDimensions.width;
        if (offset.y < 0) offset.y = 0;
        else if (offset.y > buttonDimensions.height)
          offset.y = buttonDimensions.height;
      }

      offset.x -= 4;
      offset.y -= 4;

      return offset;
    };

    const getAnimation = (startInBounds = false) => {
      const offset = getPointerOffset(startInBounds);

      const animations = {
        style: {
          opacity: 0,
          width: "8px",
          height: "8px",
          borderRadius: "4px",
          transform: `translate(${offset.x}px, ${offset.y}px)`,
        },
        initial: {
          opacity: 0,
          width: 8,
          height: 8,
          borderRadius: 4,
          x: offset.x,
          y: offset.y,
        },
        animate: {
          opacity: hoverOpacity,
          width: "100%",
          height: "100%",
          x: 0,
          y: 0,
          borderRadius: borderRadius,
        },
        exit: {
          opacity: 0,
          width: 8,
          height: 8,
          borderRadius: 4,
          x: offset.x,
          y: offset.y,
        },
      };

      return animations;
    };

    useLayoutEffect(() => {
      if ((shouldHover || forceHover) && !hovering) {
        setHoverAnimation(getAnimation(selecting));
        setHovering(true);
      } else if (!forceHover && !shouldHover && hovering) {
        setHoverAnimation((currentAnimation) => {
          const offset = getPointerOffset(selecting);

          currentAnimation.exit.x = offset.x;
          currentAnimation.exit.y = offset.y;

          return currentAnimation;
        });
        setHovering(false);
      }

      // Aug 2021 → we’re doing a clean slate of all warnings and ain’t got time to look at this so that’s why this is disabled
      // eslint-disable-next-line
    }, [shouldHover, hovering, forceHover]);

    useLayoutEffect(() => {
      if (selected && !selecting) {
        setSelectedAnimation(getAnimation());
        setSelecting(true);
      } else if (!selected && selecting && selectedAnimation) {
        setSelectedAnimation((currentAnimation) => {
          const offset = getPointerOffset(true);
          console.log(offset);
          currentAnimation.exit.x = offset.x;
          currentAnimation.exit.y = offset.y;

          return currentAnimation;
        });
        setSelecting(false);
      }

      // Aug 2021 → we’re doing a clean slate of all warnings and ain’t got time to look at this so that’s why this is disabled
      // eslint-disable-next-line
    }, [selected]);

    return (
      <motion.div
        className={"FnrHoverButton " + (selected ? "on" : "")}
        onClick={onClick}
        ref={buttonRef}
        onMouseMove={updatePointerPositionFromMouseEvent}
        data-cy="hover_btn"
        {...rest}
      >
        <AnimatePresence>
          {hovering && hoverAnimations && !disabled && (
            <motion.div
              key="hover"
              className="hover"
              {...hoverAnimations}
              style={bgColor && { backgroundColor: bgColor }}
              data-cy="hover_btn_selected"
              role="presentation"
            />
          )}
          {selecting && selectedAnimation && (
            <motion.div
              key="selected"
              className="selected"
              role="presentation"
              data-cy="hover_btn_selected"
              {...selectedAnimation}
            />
          )}
        </AnimatePresence>
        <motion.div
          className="inside"
          whileTap={{
            scale: 0.88,
          }}
          onHoverStart={onHoverStart}
          onHoverEnd={onHoverEnd}
          style={style}
        >
          {children}
        </motion.div>
      </motion.div>
    );
  }
);

FnrHoverButton.displayName = "FnrHoverButton";
export default FnrHoverButton;
